import { useState, useMemo } from 'react';
import { FilterMatchMode } from 'primereact/api';
import { useGetDealerProcessingReportQuery } from '../../../../api/endpoints/dealerProcessingReport';
import { useLanguage } from '../../../../contexts/LanguageContext';
import { matchModeToOperator } from '../../../../utils/filterUtils';

const INITIAL_FILTER_STATE = {
    yil: null,
    yilMatchMode: FilterMatchMode.CONTAINS,
    ay: null,
    ayMatchMode: FilterMatchMode.CONTAINS,
    bosBranchCode: null,
    bosBranchCodeMatchMode: FilterMatchMode.CONTAINS,
    bayiAdi: null,
    bayiAdiMatchMode: FilterMatchMode.CONTAINS,
    karsanGarantiliParca: null,
    karsanGarantiliParcaMatchMode: FilterMatchMode.CONTAINS,
    musteriOderParca: null,
    musteriOderParcaMatchMode: FilterMatchMode.CONTAINS,
    servisGarantiliParca: null,
    servisGarantiliParcaMatchMode: FilterMatchMode.CONTAINS,
    karsanGarantiliLabor: null,
    karsanGarantiliLaborMatchMode: FilterMatchMode.CONTAINS,
    musteriOderLabor: null,
    musteriOderLaborMatchMode: FilterMatchMode.CONTAINS,
    servisGarantiliLabor: null,
    servisGarantiliLaborMatchMode: FilterMatchMode.CONTAINS,
    karsanGarantiliOther: null,
    karsanGarantiliOtherMatchMode: FilterMatchMode.CONTAINS,
    musteriOderOther: null,
    musteriOderOtherMatchMode: FilterMatchMode.CONTAINS,
    servisGarantiliOther: null,
    servisGarantiliOtherMatchMode: FilterMatchMode.CONTAINS,
    karsanParcaTutari: null,
    karsanParcaTutariMatchMode: FilterMatchMode.CONTAINS,
    musteriParcaTutari: null,
    musteriParcaTutariMatchMode: FilterMatchMode.CONTAINS,
    servisParcaTutari: null,
    servisParcaTutariMatchMode: FilterMatchMode.CONTAINS,
    karsanLaborTutari: null,
    karsanLaborTutariMatchMode: FilterMatchMode.CONTAINS,
    musteriLaborTutari: null,
    musteriLaborTutariMatchMode: FilterMatchMode.CONTAINS,
    servisLaborTutari: null,
    servisLaborTutariMatchMode: FilterMatchMode.CONTAINS,
    karsanOtherTutari: null,
    karsanOtherTutariMatchMode: FilterMatchMode.CONTAINS,
    musteriOtherTutari: null,
    musteriOtherTutariMatchMode: FilterMatchMode.CONTAINS,
    servisOtherTutari: null,
    servisOtherTutariMatchMode: FilterMatchMode.CONTAINS
};

const backendColumnMap = {
    yil: 'Yil',
    ay: 'Ay',
    bosBranchCode: 'BosBranchCode',
    bayiAdi: 'BayiAdi',
    karsanGarantiliParca: 'KarsanGarantiliParca',
    musteriOderParca: 'MusteriOderParca',
    servisGarantiliParca: 'ServisGarantiliParca',
    karsanGarantiliLabor: 'KarsanGarantiliLabor',
    musteriOderLabor: 'MusteriOderLabor',
    servisGarantiliLabor: 'ServisGarantiliLabor',
    karsanGarantiliOther: 'KarsanGarantiliOther',
    musteriOderOther: 'MusteriOderOther',
    servisGarantiliOther: 'ServisGarantiliOther',
    karsanParcaTutari: 'KarsanParcaTutari',
    musteriParcaTutari: 'MusteriParcaTutari',
    servisParcaTutari: 'ServisParcaTutari',
    karsanLaborTutari: 'KarsanLaborTutari',
    musteriLaborTutari: 'MusteriLaborTutari',
    servisLaborTutari: 'ServisLaborTutari',
    karsanOtherTutari: 'KarsanOtherTutari',
    musteriOtherTutari: 'MusteriOtherTutari',
    servisOtherTutari: 'ServisOtherTutari'
};

const TEXT_FIELDS = [
    'yil',
    'ay',
    'bosBranchCode',
    'bayiAdi',
    'karsanGarantiliParca',
    'musteriOderParca',
    'servisGarantiliParca',
    'karsanGarantiliLabor',
    'musteriOderLabor',
    'servisGarantiliLabor',
    'karsanGarantiliOther',
    'musteriOderOther',
    'servisGarantiliOther',
    'karsanParcaTutari',
    'musteriParcaTutari',
    'servisParcaTutari',
    'karsanLaborTutari',
    'musteriLaborTutari',
    'servisLaborTutari',
    'karsanOtherTutari',
    'musteriOtherTutari',
    'servisOtherTutari'
];

const getBackendColumnName = (field) => {
    if (!field) return null;
    return backendColumnMap[field] || null;
};

const getOperatorValue = (matchMode) => {
    return matchModeToOperator[matchMode] || matchModeToOperator[FilterMatchMode.CONTAINS];
};
const NUMERIC_FILTER_FIELDS = new Set(
    TEXT_FIELDS.filter((field) => !['bosBranchCode', 'bayiAdi'].includes(field))
);

const normalizeNumericFilterValue = (value) => {
    const rawValue = String(value).trim();
    if (!rawValue) return '';

    return rawValue
        .replace(/\s/g, '')
        .replace(/\./g, '')
        .replace(',', '.');
};

const buildDynamicFilters = (filtersState) => {
    const filters = [];

    const pushFilter = (field, value, matchMode) => {
        if (value === null || value === undefined || value === '') return;

        const column = getBackendColumnName(field);
        if (!column) return;

        const filterValue = NUMERIC_FILTER_FIELDS.has(field)
            ? normalizeNumericFilterValue(value)
            : String(value).trim();

        if (filterValue === '') return;

        filters.push({
            column,
            operator: getOperatorValue(matchMode),
            value: filterValue
        });
    };

    TEXT_FIELDS.forEach((field) => {
        pushFilter(field, filtersState?.[field], filtersState?.[`${field}MatchMode`]);
    });

    return filters;
};
const buildDynamicSorts = (field, order) => {
    if (!field || order === 0) return null;
    const column = getBackendColumnName(field);
    if (!column) return null;
    return [{ column, direction: order === 1 ? 1 : 2 }];
};

const useDealerProcessingReportData = () => {
    const { getCurrentLanguageId } = useLanguage();

    const [lazyParams, setLazyParams] = useState({
        first: 0,
        rows: 20,
        page: 1
    });

    const [sortField, setSortField] = useState(null);
    const [sortOrder, setSortOrder] = useState(0);
    const [activeFilters, setActiveFilters] = useState(INITIAL_FILTER_STATE);

    const queryParams = useMemo(() => {
        const params = { pageNumber: lazyParams.page, pageSize: lazyParams.rows };
        const filters = buildDynamicFilters(activeFilters);
        if (filters.length > 0) params.filters = filters;
        const sorts = buildDynamicSorts(sortField, sortOrder);
        if (sorts) params.sorts = sorts;
        return params;
    }, [activeFilters, lazyParams.page, lazyParams.rows, sortField, sortOrder]);

    const { data: apiData, isLoading, isFetching } = useGetDealerProcessingReportQuery(queryParams);

    const handleFilterChange = (updatedFilters) => {
        setActiveFilters(updatedFilters);
        setLazyParams((prev) => ({ ...prev, first: 0, page: 1 }));
    };

    const initFilters = () => {
        setActiveFilters(INITIAL_FILTER_STATE);
        setSortField(null);
        setSortOrder(0);
        setLazyParams({ first: 0, rows: 20, page: 1 });
    };

    const onPage = (event) => {
        setLazyParams({
            first: event.first,
            rows: event.rows,
            page: event.page + 1
        });
    };

    const handleSortChange = (event) => {
        const nextSortField = event?.sortField || null;
        const nextSortOrder = event?.sortOrder || 0;
        const backendColumn = getBackendColumnName(nextSortField);

        if (!backendColumn && nextSortOrder !== 0) {
            return;
        }

        setSortField(nextSortField);
        setSortOrder(nextSortOrder);
        setLazyParams((prev) => ({ ...prev, first: 0, page: 1 }));
    };

    const exportParams = useMemo(() => {
        const params = { LanguageId: getCurrentLanguageId() };
        const filters = buildDynamicFilters(activeFilters);
        if (filters.length > 0) params.filters = filters;
        const sorts = buildDynamicSorts(sortField, sortOrder);
        if (sorts) params.sorts = sorts;
        return params;
    }, [activeFilters, sortField, sortOrder, getCurrentLanguageId]);

    return {
        apiData,
        isLoading: isLoading || isFetching,
        lazyParams,
        sortField,
        sortOrder,
        activeFilters,
        onPage,
        onSort: handleSortChange,
        handleFilterChange,
        initFilters,
        exportParams
    };
};

export default useDealerProcessingReportData;
